// Imports
import { Pagination, PostsOrPages } from '@tryghost/content-api';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import Button, { styling, StylingProps } from '../../../../shareables/foundation/front-end/components/buttons/standard';
import commonActions from '../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import ghost from '../../../../shareables/integrations/ghost';


// Styled components
const sharedButtonStyle = css`
	border-radius: 499.95rem;
	background: hsl(96, 54%, 59%);
	padding: 0.75rem 1rem;
	margin-top: 3.75rem;
`;

const StyledLink = styled(Link).withConfig({
	shouldForwardProp: (prop) => (prop as string) !== 'isLoading',
})<StylingProps>`
	${styling}
	${sharedButtonStyle}
`;

const StyledButton = styled(Button)`
	${sharedButtonStyle}
`;


// Define the accepted props
interface Props {
	update: (response: PostsOrPages) => void;
	initialPagination: Pagination;
	filter: string;
}


// Function component
const LoadMoreButton: React.FC<Props> = ({ update, initialPagination, filter }) => {
	// Use state
	const [isLoading, setIsLoading] = useState(false);
	const [pagination, setPagination] = useState(initialPagination);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const [searchParams, setSearchParams] = useSearchParams();
	
	
	// Update initial pagination on change
	useEffect(() => {
		setPagination(initialPagination);
	}, [initialPagination]);
	
	
	// Handle API call
	const handleAPICall = () => {
		if (!pagination.next || isLoading) {
			return;
		}
		
		setIsLoading(true);
		
		ghost.posts
			.browse({
				...(filter ? { filter } : {}),
				limit: 4,
				include: ['authors', 'tags'],
				page: pagination.next,
			})
			.then((response) => {
				setPagination(response.meta.pagination);
				update(response);
			})
			.catch(() => {
				dispatch(
					commonActions.showPageMessage({
						color: 'danger',
						title: 'Oh snap!',
						message: 'There was an unexpected issue.',
					})
				);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};
	
	
	// Do not display the button if there's no more posts
	if (pagination.next === null) {
		return null;
	}
	
	
	// Helper function that builds a link with updated params
	const generateLink = (page: number) => {
		const params = new URLSearchParams(searchParams);
		params.set('page', String(page));
		return `?${params.toString()}`;
	};
	
	
	// Initialize
	const isCategoryFiltered = Boolean(searchParams.get('view'));
	
	
	// Handle click
	const handleClick = () => {
		if (isCategoryFiltered) {
			const params = new URLSearchParams(searchParams);
			params.set('page', String(pagination.next));
			setSearchParams(params, { replace: true });
		}
		
		handleAPICall();
	};
	
	
	// Return JSX
	return isCategoryFiltered ? (
		<StyledButton type='button' isLoading={isLoading} onClick={handleClick}>
			Load more articles…
		</StyledButton>
	) : (
		<StyledLink to={generateLink(pagination.next)} replace isLoading={isLoading} onClick={handleClick}>
			Load more articles…
		</StyledLink>
	);
};

export default LoadMoreButton;
